import { ReactElement, useEffect, useState } from "react";
import { KnownScopeDto } from "../../../../data/clients/ExternalApiClient";
import { ConfirmDialog, isAdminScope } from "../shared/ConfirmDialog";

interface Props {
  knownScopes: KnownScopeDto[];
  initialSelected: string[];
  onSave: (scopes: string[]) => Promise<void>;
}

export function ScopeGrid({
  knownScopes,
  initialSelected,
  onSave,
}: Props): ReactElement {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(initialSelected),
  );
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingAdminScope, setPendingAdminScope] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setSelected(new Set(initialSelected));
  }, [initialSelected]);

  function applyToggle(scope: string) {
    const next = new Set(selected);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    setSelected(next);
  }

  function toggle(scope: string) {
    const turningOn = !selected.has(scope);
    if (turningOn && isAdminScope(scope)) {
      setPendingAdminScope(scope);
      return;
    }
    applyToggle(scope);
  }

  const dirty =
    selected.size !== initialSelected.length ||
    !initialSelected.every((s) => selected.has(s));

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      await onSave(Array.from(selected));
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Scopes
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {selected.size} af {knownScopes.length} aktiveret.
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {knownScopes.map((s) => {
          const enabled = selected.has(s.scope);
          return (
            <button
              key={s.scope}
              onClick={() => toggle(s.scope)}
              className="w-full text-left px-5 py-3 grid grid-cols-[1fr_auto] items-center gap-4 hover:bg-slate-50/60 transition-colors"
            >
              <div className="min-w-0">
                <div className="font-mono text-xs text-slate-900">
                  {s.scope}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {s.routes.length} {s.routes.length === 1 ? "rute" : "ruter"}
                  {" · "}
                  {s.routes
                    .slice(0, 3)
                    .map((r) => `${r.method} ${r.route}`)
                    .join(" · ")}
                  {s.routes.length > 3 ? " · …" : ""}
                </div>
              </div>
              <div
                className={`relative h-5 w-9 rounded-full border transition-colors shrink-0 ${
                  enabled
                    ? "bg-emerald-500 border-emerald-500"
                    : "bg-slate-200 border-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white transition-all ${
                    enabled ? "left-[18px]" : "left-0.5"
                  }`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mx-5 mb-3 px-3 py-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/40">
        {savedAt && Date.now() - savedAt < 4000 && (
          <span className="text-xs text-emerald-600">Gemt.</span>
        )}
        <button
          onClick={handleSave}
          disabled={busy || !dirty}
          className="px-4 py-1.5 text-xs font-semibold text-white bg-success hover:brightness-95 disabled:bg-slate-300 disabled:hover:brightness-100 rounded-sm transition-all"
        >
          {busy ? "Gemmer…" : dirty ? "Gem scopes" : "Ingen ændringer"}
        </button>
      </div>

      <ConfirmDialog
        open={pendingAdminScope !== null}
        title="Giv admin-adgang?"
        body={
          <>
            Scopet{" "}
            <code className="font-mono text-xs px-1 py-0.5 bg-slate-100 rounded-sm">
              {pendingAdminScope}
            </code>{" "}
            giver denne nøgle adgang til admin-endpoints — oprette og
            tilbagekalde API-nøgler, redigere prisplaner, ændre scopes på
            andre forhandlere. Giv kun dette til nøgler, der bruges af
            betroede interne medarbejdere eller selve admin-frontenden.
          </>
        }
        confirmLabel="Giv admin-scope"
        destructive
        onCancel={() => setPendingAdminScope(null)}
        onConfirm={() => {
          if (pendingAdminScope) applyToggle(pendingAdminScope);
          setPendingAdminScope(null);
        }}
      />
    </div>
  );
}
